import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import {
  ApplyWorkflowStagesDocument,
  type ApplyWorkflowStagesInput,
  UpdateStageDocument,
  type UpdateStageInput,
} from "#app/lib/data";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

export function useStageMutations() {
  const { track } = useAnalytics();

  const [updateStageMutation] = useMutation(UpdateStageDocument);

  // The stage-manager dialog's one commit. The success branch returns the
  // workflow's full stage list, so kept rows re-normalize by stageId; deleted
  // rows are evicted here. Creates land through the refetches: `stages` is
  // cached per workflow (and the workspace bootstrap holds its own list), so
  // an unscoped insertIntoConnection would land the new stage in OTHER
  // workflows' cached lists.
  // GetWorkflow rides along for its `stages.total` — the workflow settings
  // page's "N stages" row reads it.
  const [applyWorkflowStagesMutation] = useMutation(ApplyWorkflowStagesDocument, {
    refetchQueries: ["StagesByWorkflow", "WorkspaceData", "GetWorkflow"],
    update(cache, { data }) {
      const result = data?.applyWorkflowStages;
      if (result?.__typename !== "ApplyWorkflowStagesSuccess") return;
      for (const stageId of result.deletedStageIds) {
        removeFromConnection(
          cache,
          "stages",
          { __typename: "Stage", stageId },
          { evict: true },
        );
      }
    },
  });

  const updateStage = useCallback(
    async (
      stageId: string,
      input: UpdateStageInput,
      /** The surface of the edit: "board_column", "table_group",
       *  "stage_cycle_menu". */
      tracking?: { via?: string },
    ) => {
      const { data } = await updateStageMutation({
        variables: { id: stageId, input },
      });
      if (data?.updateStage?.__typename === "Stage") {
        track({
          context: AnalyticsContext.Settings,
          event: SettingsEvent.Update,
          label: "stage",
          data: { fields: Object.keys(input), via: tracking?.via ?? "unknown" },
        });
      }
      return data?.updateStage ?? null;
    },
    [updateStageMutation, track],
  );

  // Promotion rides the ordinary update, like `setDefaultWorkflow`. The
  // refetch picks up the stage that gave the flag up — only the promoted row
  // comes back.
  const setDefaultStage = useCallback(
    async (stageId: string) => {
      const { data } = await updateStageMutation({
        variables: { id: stageId, input: { isDefault: true } },
        refetchQueries: ["StagesByWorkflow", "WorkspaceData"],
      });
      if (data?.updateStage?.__typename === "Stage") {
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.SetDefault, label: "stage" });
      }
      return data?.updateStage ?? null;
    },
    [updateStageMutation, track],
  );

  const applyWorkflowStages = useCallback(
    async (workflowId: string, input: ApplyWorkflowStagesInput) => {
      const { data } = await applyWorkflowStagesMutation({
        variables: { workflowId, input },
      });
      const result = data?.applyWorkflowStages;
      if (result?.__typename === "ApplyWorkflowStagesSuccess") {
        // The one commit replaced separate create/delete/reorder events, so
        // it carries their counts.
        track({
          context: AnalyticsContext.Settings,
          event: SettingsEvent.Update,
          label: "stages",
          data: {
            created: input.stages.filter((stage) => !stage.id).length,
            deleted: result.deletedStageIds.length,
            total: input.stages.length,
          },
        });
      }
      return result ?? null;
    },
    [applyWorkflowStagesMutation, track],
  );

  return {
    updateStage,
    setDefaultStage,
    applyWorkflowStages,
  };
}
